import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from "../../../../lib/react-leaflet-lite";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  MapPin, Plus, Edit2, Trash2, Save, Loader2, Building2, AlertCircle,
} from "lucide-react";
import { updateLogisticsCompany } from "../../../../api/settings";
import { showToast } from "../../../../utils/showToast";
import { useTheme } from "../../../../hooks/useTheme";
import { useBasemapTile } from "../../../../config/basemaps";
import { Switch } from "../../../../components/ui/Switch";
import { Modal } from "../../../../components/ui/Modal";
import { Button } from "../../../../components/ui/Button";
import { createTeardropMarker } from "../../../../utils/mapHelpers";
import {
  searchAddresses, geocodeAddress, resolveAddress, addressLatLng, addressToQuery,
  geocodeDefaultsFor,
} from "../../../../utils/geocode";
import { useCountry } from "../../../../context/CountryContext";

// ─────────────────────────────────────────────────────────────────────────────
// Fix Leaflet default icon (webpack asset issue)
// ─────────────────────────────────────────────────────────────────────────────

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

// Migrado a createTeardropMarker (audit #8). Pin de ubicación sin contenido.
const customMarker = (color = "#F97316") =>
  createTeardropMarker({ size: 24, color, borderColor: '#fff', borderWidth: 2 });

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const ADDRESS_TYPES = ["Bodega", "Oficina", "Punto de retiro", "Centro de distribución", "Otro"];

// El formulario arranca con el país de operación del operador. Con "Chile" fijo,
// un operador argentino escribía direcciones que el geocoder buscaba en el país
// equivocado (o no encontraba en absoluto).
const emptyForm = (countryName) => ({
  type: "", address: "", municipality: "", state: "", country: countryName || "Chile",
  shippingLocation: false, lat: null, lng: null,
});

/**
 * Hook con las opciones de geocodificación del país de operación:
 * `{ countryCode, center }` para `boundary.country` y `focus.point`, más el
 * centro del mapa en formato Leaflet [lat, lng].
 */
function useGeoDefaults() {
  const { countryCode, countryName } = useCountry();
  return useMemo(() => {
    const defaults = geocodeDefaultsFor(countryCode);
    return {
      opts: { countryCode: defaults.countryCode, center: defaults.center },
      mapCenter: [defaults.center.lat, defaults.center.lng],
      countryName,
    };
  }, [countryCode, countryName]);
}

// Clave estable para cachear la geocodificación de direcciones sin coordenadas.
const addressKey = (a) => `${a?.address}|${a?.municipality}|${a?.state}|${a?.country}`;

// ─────────────────────────────────────────────────────────────────────────────
// Map helper components
// ─────────────────────────────────────────────────────────────────────────────

function MapClickHandler({ onMapClick }) {
  useMapEvents({ click: (e) => onMapClick(e.latlng) });
  return null;
}

// Ajusta la vista del mapa para encuadrar todos los marcadores. Un solo punto →
// setView; varios → fitBounds. Reacciona a cambios en el conjunto de puntos.
function FitBounds({ points }) {
  const map = useMap();
  const key = points.map((p) => `${p[0]},${p[1]}`).join("|");
  useEffect(() => {
    if (!map || points.length === 0) return;
    if (points.length === 1) {
      map.setView(points[0], 15, { animate: false });
    } else {
      map.fitBounds(points, { padding: [48, 48], maxZoom: 16, animate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, key]);
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Address form (modal) — con autocompletado y selector de mapa
// ─────────────────────────────────────────────────────────────────────────────

function AddressModal({ initial, onClose, onSave, isSaving }) {
  const { t } = useTranslation();
  const { opts: geoOpts, mapCenter, countryName } = useGeoDefaults();
  const [form, setForm] = useState(() => initial ?? emptyForm(countryName));
  const [suggestions, setSuggestions] = useState([]);
  const [activeIdx, setActiveIdx] = useState(-1); // sugerencia resaltada (teclado)
  const [loadingSug, setLoadingSug] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [resolving, setResolving] = useState(false); // resolviendo coords de una sugerencia (Google)
  const isNew = !initial;
  const { isDark } = useTheme();
  const tile = useBasemapTile(isDark);

  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  const pickerMapRef = useRef(null);

  const set = (field, value) => setForm((p) => ({ ...p, [field]: value }));
  const point = useMemo(() => addressLatLng({ lat: form.lat, lng: form.lng }), [form.lat, form.lng]);

  const clearPending = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.abort();
  }, []);

  const closeSuggestions = useCallback(() => {
    setSuggestions([]);
    setActiveIdx(-1);
  }, []);

  // Búsqueda de sugerencias con debounce + cancelación de peticiones obsoletas.
  const runSearch = useCallback((query) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const term = (query || "").trim();
    if (term.length < 3) {
      setSuggestions([]);
      setActiveIdx(-1);
      setLoadingSug(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setLoadingSug(true);
      try {
        const data = await searchAddresses(term, { ...geoOpts, signal: controller.signal });
        setSuggestions(data);
        setActiveIdx(-1);
      } catch {
        setSuggestions([]);
        setActiveIdx(-1);
      } finally {
        setLoadingSug(false);
      }
    }, 300);
  }, [geoOpts]);

  useEffect(() => clearPending, [clearPending]);

  // Al editar una dirección legada sin coordenadas: geocodificar al abrir para
  // sembrar el pin (el usuario puede ajustarlo). No pisa un pin ya existente.
  useEffect(() => {
    if (isNew || addressLatLng(form) || !form.address || !form.municipality) return;
    let cancelled = false;
    (async () => {
      const g = await geocodeAddress(addressToQuery(form), geoOpts);
      if (cancelled || !g) return;
      setForm((p) => (addressLatLng(p) ? p : { ...p, lat: g.lat, lng: g.lng }));
      pickerMapRef.current?.flyTo([g.lat, g.lng], 16, { animate: false });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Escribir en el campo de dirección solo actualiza el texto y busca sugerencias;
  // NO borra un pin ya colocado (por sugerencia, clic o arrastre) — el usuario lo
  // ve en el mapa y puede reubicarlo. Si nunca se fijó un pin, se geocodifica el
  // texto al guardar (handleSubmit).
  const handleAddressChange = (value) => {
    set("address", value);
    runSearch(value);
  };

  const selectSuggestion = async (s) => {
    clearPending();
    closeSuggestions();
    let lat = s.lat;
    let lng = s.lng;
    // Algunas sugerencias (típicamente de Google) llegan sin coords y se resuelven
    // con una llamada extra a /v1/resolve.
    if (s.needsResolution || lat == null || lng == null) {
      setResolving(true);
      const r = await resolveAddress(s.id, { source: s.source });
      setResolving(false);
      if (!r) {
        showToast(t("No pudimos obtener la ubicación de esa dirección. Intenta con otra o marca el punto en el mapa."), false);
        return;
      }
      lat = r.lat;
      lng = r.lng;
    }
    setForm((p) => ({
      ...p,
      address: s.label || p.address,
      // Solo rellena si el campo está vacío — nunca pisa lo que escribió el usuario.
      municipality: p.municipality || s.locality || "",
      state: p.state || s.region || "",
      lat,
      lng,
    }));
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      pickerMapRef.current?.flyTo([lat, lng], 16, { animate: true });
    }
  };

  // Navegación por teclado del autocompletado (patrón combobox: el foco se queda
  // en el input y la opción activa se referencia con aria-activedescendant).
  const handleAddressKeyDown = (e) => {
    if (e.key === "Escape" && suggestions.length > 0) {
      e.stopPropagation(); // no cerrar el modal, solo el dropdown
      closeSuggestions();
      return;
    }
    if (suggestions.length === 0) return; // Enter cae al submit normal (geocode al guardar)
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === "Enter" && activeIdx >= 0) {
      e.preventDefault(); // elegir la opción resaltada en vez de enviar el form
      selectSuggestion(suggestions[activeIdx]);
    }
  };

  // Click en el mapa o arrastre del pin → fija coordenadas manualmente.
  const setPoint = (latlng) => {
    const lat = Array.isArray(latlng) ? latlng[0] : latlng.lat;
    const lng = Array.isArray(latlng) ? latlng[1] : latlng.lng;
    setForm((p) => ({ ...p, lat, lng }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    const country = (form.country || "").trim();
    if (!form.type || !form.address || !form.municipality || !form.state || !country) {
      showToast(t("Completa todos los campos obligatorios"), false);
      return;
    }
    let { lat, lng } = form;
    // Sin coordenadas aún: intenta geocodificar el texto ingresado antes de guardar.
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      setGeocoding(true);
      const g = await geocodeAddress(addressToQuery({ ...form, country }), geoOpts);
      setGeocoding(false);
      if (g) {
        lat = g.lat;
        lng = g.lng;
      } else {
        showToast(t("No pudimos ubicar la dirección en el mapa. Marca el punto haciendo clic en el mapa."), false);
        return;
      }
    }
    onSave({ ...form, country, lat, lng });
  };

  const busy = isSaving || geocoding || resolving;
  const showDropdown = loadingSug || suggestions.length > 0;

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      title={isNew ? t("Agregar ubicación") : t("Editar ubicación")}
      bodyClassName="p-0"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            {t("Cancelar")}
          </Button>
          <Button variant="primary" onClick={handleSubmit} loading={busy} iconLeft={<Save size={16} />}>
            {(geocoding || resolving) ? t("Ubicando…") : isSaving ? t("Guardando…") : t("Guardar")}
          </Button>
        </>
      }
    >
      <div className="pt-modal pt-modal--embedded">
        <form onSubmit={handleSubmit}>
          <div className="pt-modal__body">
            {/* Tipo */}
            <label className="pt-field">
              <span className="pt-field__label">{t("Tipo *")}</span>
              <select
                className="pt-field__input"
                value={form.type}
                onChange={(e) => set("type", e.target.value)}
                required
              >
                <option value="">{t("Selecciona un tipo")}</option>
                {ADDRESS_TYPES.map((type) => <option key={type} value={type}>{t(type)}</option>)}
              </select>
            </label>

            {/* Dirección (con autocompletado) */}
            <div className="pt-field lt-address-field">
              <span className="pt-field__label" id="lt-address-label">{t("Dirección *")}</span>
              <input
                className="pt-field__input"
                value={form.address}
                onChange={(e) => handleAddressChange(e.target.value)}
                onKeyDown={handleAddressKeyDown}
                placeholder={t("Ej: Av. Providencia 1234")}
                autoComplete="off"
                role="combobox"
                aria-expanded={suggestions.length > 0}
                aria-controls="lt-suggestions-list"
                aria-autocomplete="list"
                aria-activedescendant={activeIdx >= 0 ? `lt-sug-${activeIdx}` : undefined}
                aria-labelledby="lt-address-label"
                required
              />
              {showDropdown && (
                <div className="lt-suggestions">
                  {loadingSug && <div className="lt-suggestions__loading">{t("Buscando…")}</div>}
                  {suggestions.length > 0 && (
                    <ul className="lt-suggestions__list" id="lt-suggestions-list" role="listbox" aria-label={t("Sugerencias de dirección")}>
                      {suggestions.map((s, idx) => (
                        <li
                          key={`${s.id}-${idx}`}
                          id={`lt-sug-${idx}`}
                          role="option"
                          aria-selected={idx === activeIdx}
                          className="lt-suggestions__item"
                          onMouseDown={(e) => { e.preventDefault(); selectSuggestion(s); }}
                          onMouseEnter={() => setActiveIdx(idx)}
                        >
                          <MapPin size={14} aria-hidden="true" />
                          <span className="lt-suggestions__text">
                            <span className="lt-suggestions__label">{s.label}</span>
                            {s.subtitle ? <span className="lt-suggestions__sub">{s.subtitle}</span> : null}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>

            <div className="pt-field-row">
              {/* Municipio */}
              <label className="pt-field">
                <span className="pt-field__label">{t("Municipio *")}</span>
                <input
                  className="pt-field__input"
                  value={form.municipality}
                  onChange={(e) => set("municipality", e.target.value)}
                  placeholder={t("Ej: Providencia")}
                  required
                />
              </label>

              {/* Estado/Región */}
              <label className="pt-field">
                <span className="pt-field__label">{t("Región *")}</span>
                <input
                  className="pt-field__input"
                  value={form.state}
                  onChange={(e) => set("state", e.target.value)}
                  placeholder={t("Ej: Metropolitana")}
                  required
                />
              </label>
            </div>

            {/* País */}
            <label className="pt-field">
              <span className="pt-field__label">{t("País *")}</span>
              <input
                className="pt-field__input"
                value={form.country}
                onChange={(e) => set("country", e.target.value)}
                placeholder={t("Ej: Chile")}
                required
              />
            </label>

            {/* Selector de ubicación en el mapa */}
            <div className="pt-field">
              <span className="pt-field__label">{t("Ubicación en el mapa *")}</span>
              <div className="lt-picker">
                <MapContainer
                  center={point ?? mapCenter}
                  zoom={point ? 15 : 11}
                  className="lt-picker__map"
                  ref={pickerMapRef}
                  aria-label={t("Selector de ubicación en el mapa")}
                >
                  <TileLayer
                    key={tile.url}
                    url={tile.url}
                    attribution={tile.attribution}
                    subdomains={tile.subdomains}
                    maxZoom={tile.maxZoom}
                  />
                  <MapClickHandler onMapClick={setPoint} />
                  {point && (
                    <Marker
                      position={point}
                      icon={customMarker()}
                      draggable
                      eventHandlers={{
                        dragend: (e) => setPoint(e.target.getLatLng()),
                      }}
                    />
                  )}
                </MapContainer>
                <p className="lt-picker__hint">
                  <MapPin size={13} aria-hidden="true" />
                  {point
                    ? t("Arrastra el pin para ajustar la ubicación exacta.")
                    : t("Busca la dirección arriba o haz clic en el mapa para marcar la ubicación.")}
                </p>
              </div>
            </div>

            {/* Punto de envío */}
            <div className="lt-toggle-field">
              <div className="lt-toggle-field__info">
                <span className="lt-toggle-field__label">{t("Punto de envío")}</span>
                <span className="lt-toggle-field__hint">{t("Los clientes pueden seleccionar esta dirección para retiros")}</span>
              </div>
              <Switch
                checked={!!form.shippingLocation}
                onChange={(next) => set("shippingLocation", next)}
                ariaLabel={t("Punto de envío")}
              />
            </div>
          </div>
          {/* Submit oculto para que Enter envíe el formulario */}
          <button type="submit" className="sr-only" tabIndex={-1} aria-hidden="true" />
        </form>
      </div>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LocationsTab
// ─────────────────────────────────────────────────────────────────────────────

export default function LocationsTab({ company, companyId, onCompanyUpdate }) {
  const { t } = useTranslation();
  const [addresses, setAddresses] = useState([]);
  const [editingIdx, setEditingIdx] = useState(null); // null = not editing; -1 = new
  const [deleteIdx, setDeleteIdx] = useState(null);
  const [saving, setSaving] = useState(false);
  // Cache de coordenadas resueltas para direcciones legadas sin lat/lng.
  // Clave = addressKey(addr); valor = [lat,lng] o null (no encontrada).
  const [geoCache, setGeoCache] = useState({});
  const { isDark } = useTheme();
  // Tile del basemap activo (fuente única configurable desde Configuración).
  const tile = useBasemapTile(isDark);
  const { opts: geoOpts, mapCenter } = useGeoDefaults();

  useEffect(() => {
    setAddresses(company?.operationsAddresses ?? []);
  }, [company]);

  // Backfill de geocodificación para direcciones sin coordenadas (datos legados
  // creados antes de esta feature). Se resuelven secuencialmente para no saturar
  // el geocoder y se aplican en UNA sola actualización al final, para que el
  // encuadre del mapa (FitBounds) se ejecute una vez y no “salte” por cada pin.
  useEffect(() => {
    const missing = addresses.filter(
      (a) => !addressLatLng(a) && !(addressKey(a) in geoCache) && a.address && a.municipality,
    );
    if (missing.length === 0) return undefined;
    let cancelled = false;
    (async () => {
      const resolved = {};
      for (const a of missing) {
        if (cancelled) return;
        const g = await geocodeAddress(addressToQuery(a), geoOpts);
        resolved[addressKey(a)] = g ? [g.lat, g.lng] : null;
      }
      if (!cancelled) setGeoCache((prev) => ({ ...prev, ...resolved }));
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addresses]);

  // Direcciones con coordenadas resueltas (propias o desde el cache de backfill).
  const markers = useMemo(
    () =>
      addresses
        .map((addr, i) => ({ addr, i, coords: addressLatLng(addr) ?? geoCache[addressKey(addr)] ?? null }))
        .filter((m) => m.coords),
    [addresses, geoCache],
  );
  const points = useMemo(() => markers.map((m) => m.coords), [markers]);

  const persistAddresses = useCallback(async (newAddresses) => {
    setSaving(true);
    try {
      const res = await updateLogisticsCompany(companyId, { operationsAddresses: newAddresses });
      const updated = res.data ?? res;
      onCompanyUpdate(updated);
      setAddresses(updated.operationsAddresses ?? newAddresses);
      showToast(t("Ubicaciones actualizadas"), true);
    } catch {
      showToast(t("No se pudo guardar la ubicación"), false);
    } finally {
      setSaving(false);
    }
  }, [companyId, onCompanyUpdate]);

  const handleSave = useCallback((form) => {
    let next;
    if (editingIdx === -1) {
      next = [...addresses, form];
    } else {
      next = addresses.map((a, i) => (i === editingIdx ? form : a));
    }
    persistAddresses(next).then(() => setEditingIdx(null));
  }, [addresses, editingIdx, persistAddresses]);

  const handleDelete = useCallback(() => {
    const next = addresses.filter((_, i) => i !== deleteIdx);
    persistAddresses(next).then(() => setDeleteIdx(null));
  }, [addresses, deleteIdx, persistAddresses]);

  if (!company) {
    return (
      <div className="as-empty">
        <Loader2 size={32} className="spin as-empty__icon" />
        <p className="as-empty__body">{t("Cargando datos de la empresa…")}</p>
      </div>
    );
  }

  return (
    <div className="w-full min-w-0 lt-root">
      {/* ── Map ───────────────────────────────────────────────────── */}
      <div className="lt-map-wrap">
        <MapContainer
          center={points[0] ?? mapCenter}
          zoom={11}
          className="lt-map"
          aria-label={t("Mapa de ubicaciones operativas")}
        >
          <TileLayer
            key={tile.url}
            url={tile.url}
            attribution={tile.attribution}
            subdomains={tile.subdomains}
            maxZoom={tile.maxZoom}
          />
          {markers.map((m) => (
            <Marker
              key={m.i}
              position={m.coords}
              icon={customMarker(m.addr.shippingLocation ? "#22c55e" : "#F97316")}
            >
              <Popup>
                <strong>{t(m.addr.type)}</strong>
                <br />
                {m.addr.address}
                <br />
                {m.addr.municipality}, {m.addr.state}
              </Popup>
            </Marker>
          ))}
          <FitBounds points={points} />
        </MapContainer>
        <p className="lt-map-hint">
          <MapPin size={13} aria-hidden="true" />
          {t("Los marcadores representan tus instalaciones registradas.")}
        </p>
      </div>

      {/* ── Header ────────────────────────────────────────────────── */}
      <div className="lt-list-header">
        <h3 className="lt-list-title">{t("Instalaciones registradas")}</h3>
        <Button
          variant="outline"
          onClick={() => setEditingIdx(-1)}
          disabled={saving}
          iconLeft={<Plus size={16} />}
        >
          {t("Agregar ubicación")}
        </Button>
      </div>

      {/* ── List ──────────────────────────────────────────────────── */}
      {addresses.length === 0 ? (
        <div className="as-empty">
          <Building2 size={40} className="as-empty__icon" />
          <h3 className="as-empty__title">{t("Sin ubicaciones registradas")}</h3>
          <p className="as-empty__body">
            {t("Agrega tus bodegas, oficinas y puntos de retiro para que aparezcan en el mapa.")}
          </p>
        </div>
      ) : (
        <div className="lt-table" role="table" aria-label={t("Ubicaciones operativas")}>
          <div className="lt-table__head" role="row">
            <span role="columnheader">{t("Tipo")}</span>
            <span role="columnheader">{t("Dirección")}</span>
            <span role="columnheader">{t("Municipio")}</span>
            <span role="columnheader">{t("Región")}</span>
            <span role="columnheader">{t("Envío")}</span>
            <span role="columnheader">{t("Acciones")}</span>
          </div>
          {addresses.map((addr, i) => (
            <div className="lt-table__row" key={i} role="row">
              <span role="cell">
                <span className="as-badge as-badge--neutral">{t(addr.type)}</span>
              </span>
              <span role="cell" className="lt-table__address">{addr.address}</span>
              <span role="cell">{addr.municipality}</span>
              <span role="cell">{addr.state}</span>
              <span role="cell">
                {addr.shippingLocation ? (
                  <span className="as-badge as-badge--success">{t("Sí")}</span>
                ) : (
                  <span className="as-badge as-badge--neutral">{t("No")}</span>
                )}
              </span>
              <span role="cell" className="lt-table__actions">
                <button
                  type="button" className="lt-action-btn"
                  onClick={() => setEditingIdx(i)}
                  aria-label={t("Editar {{address}}", { address: addr.address })}
                >
                  <Edit2 size={15} />
                </button>
                <button
                  type="button" className="lt-action-btn lt-action-btn--danger"
                  onClick={() => setDeleteIdx(i)}
                  aria-label={t("Eliminar {{address}}", { address: addr.address })}
                >
                  <Trash2 size={15} />
                </button>
              </span>
            </div>
          ))}
        </div>
      )}

      {/* ── Edit/Add modal ────────────────────────────────────────── */}
      {editingIdx !== null && (
        <AddressModal
          initial={editingIdx === -1 ? null : addresses[editingIdx]}
          onClose={() => setEditingIdx(null)}
          onSave={handleSave}
          isSaving={saving}
        />
      )}

      {/* ── Delete confirmation ───────────────────────────────────── */}
      {deleteIdx !== null && (
        <Modal
          open
          onClose={() => setDeleteIdx(null)}
          size="sm"
          title={t("Eliminar ubicación")}
          bodyClassName="p-0"
          footer={
            <>
              <Button variant="outline" onClick={() => setDeleteIdx(null)}>
                {t("Cancelar")}
              </Button>
              <Button variant="danger" onClick={handleDelete} loading={saving} iconLeft={<Trash2 size={16} />}>
                {t("Eliminar")}
              </Button>
            </>
          }
        >
          <div className="pt-modal pt-modal--embedded">
            <div className="pt-modal__body">
              <div className="lt-delete-confirm">
                <AlertCircle size={24} className="text-status-error shrink-0" />
                <p>
                  {t("¿Estás seguro de que quieres eliminar")}{" "}
                  <strong>{addresses[deleteIdx]?.address}</strong>?
                  {t("Esta acción no se puede deshacer.")}
                </p>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
